// src/components/CapybaraStudio.tsx
// a client-side precheck, based on the local coin balance.
// It gives the user instant feedback and prevents obviously invalid requests.
// However, it’s not authoritative — we don’t trust this value for the final deduction.

import React, { useState, useEffect, useMemo } from "react";
import WardrobeCatalog from "./WardrobeCatalog";
import CapybaraCanvas from "./CapybaraCanvas";
import { wardrobeItems } from "../data/wardrobeItems";
import { N8N_UPDATE_COINS_ITEMS_URL } from "../config/n8n"; // ✅ centralized import
import { X } from "lucide-react";

interface CapybaraStudioProps {
  open: boolean;
  onClose: () => void;
  coins?: number;
  unlockedItems: string[];
  equippedItems: string[];
  userId?: string;

  // ✅ NEW: notify parent so main window/header stays in sync
  onCoinsChange?: (coins: number) => void;
  onUnlockedItemsChange?: (items: string[]) => void;
  onEquippedItemsChange?: (items: string[]) => void;
}

type PendingUnlock =
  | {
    category: string;
    overlayFile: string; // e.g. "hats/baseball_cap_red_overlay.png"
    cost: number;
  }
  | null;

export default function CapybaraStudio({
  open,
  onClose,
  coins: startingCoins = 0,
  unlockedItems: initialUnlocked,
  equippedItems: initialEquipped,
  userId = "unknown-user",

  // ✅ NEW
  onCoinsChange,
  onUnlockedItemsChange,
  onEquippedItemsChange,
}: CapybaraStudioProps) {
  // Local state for immediate updates (coins, unlocked, equipped)
  const [coins, setCoins] = useState<number>(startingCoins);
  const [unlockedItems, setUnlockedItems] = useState<string[]>(initialUnlocked || []);
  const [selectedItems, setSelectedItems] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (initialEquipped || []).map((path) => {
        const [category] = path.split("/");
        return [category, path];
      })
    )
  );

  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [pendingUnlock, setPendingUnlock] = useState<PendingUnlock>(null);
  const [unlockError, setUnlockError] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  // 🧩 Sync with incoming props when parent updates
  useEffect(() => {
    setCoins(startingCoins);
    setUnlockedItems(initialUnlocked || []);
    setSelectedItems(
      Object.fromEntries(
        (initialEquipped || []).map((path) => {
          const [category] = path.split("/");
          return [category, path];
        })
      )
    );
  }, [startingCoins, initialUnlocked, initialEquipped]);

  // ✅ Determine if outfit has changed (add/remove/replace)
  const isDirty = useMemo(() => {
    const current = Object.values(selectedItems).sort();
    const initial = (initialEquipped || []).sort();
    if (current.length !== initial.length) return true;
    return !current.every((v, i) => v === initial[i]);
  }, [selectedItems, initialEquipped]);

  // ✅ Toggle wardrobe item
  function handleSelect(category: string, overlayFile: string) {
    setSelectedItems((prev) => {
      const currentlySelected = prev[category];
      if (currentlySelected === overlayFile) {
        const updated = { ...prev };
        delete updated[category];
        return updated;
      } else {
        return { ...prev, [category]: overlayFile };
      }
    });
  }

  // Handle clicking a locked item
  function handleLockedClick(category: string, overlayFile: string) {
    const [cat, overlayName] = overlayFile.split("/");
    const baseName = overlayName.replace("_overlay.png", ".png");
    const item = wardrobeItems.find(
      (w) => w.category === cat && w.file === baseName
    );
    const cost = item?.cost ?? 0;
    setUnlockError("");
    setPendingUnlock({ category, overlayFile, cost });
  }

  // ✅ Confirm unlock (server-authoritative + local mirror + parent sync)
  async function confirmUnlock() {
    if (!pendingUnlock) return;
    const { overlayFile, cost } = pendingUnlock;

    setUnlockError("");
    setSaveMessage("");

    // 🧠 Quick client pre-check — stop if obviously insufficient
    if (coins < cost) {
      setUnlockError("Not enough coins to unlock this item.");
      return;
    }

    setUnlocking(true);

    try {
      const res = await fetch(N8N_UPDATE_COINS_ITEMS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "unlock",
          userId,
          accessCode: localStorage.getItem("accessCode") || "",
          item: overlayFile,
          cost,
        }),
      });

      const data = await res.json();

      if (data.status === "success") {
        // ✅ Backend/Airtable is authoritative; local is a mirror fallback
        const updatedCoins = data.coins ?? Math.max(0, coins - cost);

        const updatedUnlockedItems = Array.from(
          new Set([
            ...(Array.isArray(data.unlockedItems) ? data.unlockedItems : unlockedItems),
            overlayFile,
          ])
        );

        setCoins(updatedCoins);
        setUnlockedItems(updatedUnlockedItems);

        // ✅ keep parent/main window in sync
        onCoinsChange?.(updatedCoins);
        onUnlockedItemsChange?.(updatedUnlockedItems);

        // ✅ Update equipped items only if backend returns them
        if (Array.isArray(data.equippedItems)) {
          const updatedSelectedItems = Object.fromEntries(
            data.equippedItems.map((p: string) => [p.split("/")[0], p])
          );
          setSelectedItems(updatedSelectedItems);
          onEquippedItemsChange?.(data.equippedItems);
        }

        setPendingUnlock(null);
        setSaveMessage("✅ Unlock successful!");
        setTimeout(() => setSaveMessage(""), 3000);
      } else {
        setUnlockError(data.error || "Failed to unlock item.");
      }
    } catch (e) {
      console.error("Unlock sync failed:", e);
      setUnlockError("Network error. Please try again.");
    } finally {
      setUnlocking(false);
    }
  }

  // ✅ Save outfit to n8n (persist equipped items + parent sync)
  async function handleSaveOutfit() {
    setSaving(true);
    setSaveMessage("");

    const equippedItems = Object.values(selectedItems);
    const payload = {
      action: "save",
      userId,
      accessCode: localStorage.getItem("accessCode") || "",
      equippedItems,
      unlockedItems,
    };

    try {
      const res = await fetch(N8N_UPDATE_COINS_ITEMS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status === "success") {
        const updatedCoins = data.coins ?? coins;
        const updatedUnlockedItems = Array.isArray(data.unlockedItems)
          ? data.unlockedItems
          : unlockedItems;
        const updatedEquippedItems = equippedItems;

        setCoins(updatedCoins);
        setUnlockedItems(updatedUnlockedItems);

        // ✅ keep parent/main window in sync
        onCoinsChange?.(updatedCoins);
        onUnlockedItemsChange?.(updatedUnlockedItems);
        onEquippedItemsChange?.(updatedEquippedItems);

        setSaveMessage("✅ Outfit saved successfully!");
      } else {
        setSaveMessage("❌ Failed to save outfit.");
      }
    } catch (err) {
      console.error("Save outfit error:", err);
      setSaveMessage("❌ Network error. Please try again.");
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMessage(""), 4000);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="bg-white rounded-xl p-6 shadow-lg relative flex flex-col gap-4">
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close Capybara Studio"
          className="absolute right-3 top-3 inline-flex h-11 w-11 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-300"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>

        {/* Header */}
        <div className="flex flex-col items-center px-12 text-center">
          <h2 className="text-xl font-semibold text-gray-900">
            Dress the Capybara
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            Choose items to customize the capybara’s outfit
          </p>

          <div className="mt-3 flex items-center justify-center gap-2 text-lg font-semibold text-yellow-600">
            <img
              src="/assets/ui/coin.png"
              alt="coin"
              className="w-6 h-6 drop-shadow-sm"
            />
            <span>{coins} Coins</span>
          </div>
        </div>

        {/* Main Layout */}
        <div className="flex gap-6">
          <WardrobeCatalog
            onSelect={handleSelect}
            selectedItems={selectedItems}
            unlockedItems={unlockedItems}
            onLockedClick={handleLockedClick}
          />
          <CapybaraCanvas selectedItems={selectedItems} />
        </div>

        {/* Save Outfit Button */}
        <div className="flex justify-center mt-4">
          <button
            onClick={handleSaveOutfit}
            disabled={saving || !isDirty}
            className={`px-4 py-1.5 rounded-md font-medium text-white transition
              ${saving || !isDirty
                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                : "bg-amber-900 hover:bg-amber-950"
              }`}
          >
            {saving ? "Saving..." : "Save Outfit"}
          </button>
        </div>

        {saveMessage && (
          <p className="text-center text-sm text-gray-600 mt-1">{saveMessage}</p>
        )}

        {/* 🔓 Unlock Modal */}
        {pendingUnlock && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/30 z-[100]">
            <div className="bg-white rounded-lg shadow-lg p-5 w-[360px] relative">
              <button
                className="absolute top-2 right-2 text-gray-500 hover:text-black"
                onClick={() => setPendingUnlock(null)}
              >
                ✕
              </button>

              <div className="flex items-center gap-2 mb-3">
                <img src="/assets/ui/coin.png" alt="coin" className="w-5 h-5" />
                <h3 className="text-lg font-semibold">Unlock item?</h3>
              </div>

              <p className="text-sm text-gray-700">
                This item costs{" "}
                <span className="font-semibold">{pendingUnlock.cost} coins</span>. You
                have <span className="font-semibold">{coins} coins</span>.
              </p>

              {/* 🧠 Instant feedback if insufficient coins */}
              {coins < pendingUnlock.cost && (
                <p className="mt-2 text-sm text-red-600">
                  Not enough coins to unlock this item.
                </p>
              )}

              {unlockError && coins >= pendingUnlock.cost && (
                <p className="mt-2 text-sm text-red-600">{unlockError}</p>
              )}

              <div className="mt-4 flex justify-end gap-2">
                <button
                  className="px-3 py-1.5 rounded-md border bg-white hover:bg-gray-50"
                  onClick={() => setPendingUnlock(null)}
                >
                  Cancel
                </button>
                <button
                  className={`px-3 py-1.5 rounded-md text-white transition ${coins < pendingUnlock.cost
                    ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                    : "bg-amber-600 hover:bg-amber-700"
                    }`}
                  onClick={coins < pendingUnlock.cost ? undefined : confirmUnlock}
                  disabled={unlocking || coins < pendingUnlock.cost}
                >
                  {unlocking ? "Unlocking..." : "Unlock"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}